import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { trackEvent } from '../utils/recommendationEngine';

const CartContext = createContext(null);

const initialState = { items: [] };

function cartReducer(state, action) {
  switch (action.type) {
    case 'SET_CART':
      return { items: action.payload };

    case 'ADD_ITEM': {
      const { product, quantity = 1, size = '' } = action.payload;
      const exists = state.items.find(
        (i) => i.product.id === product.id && i.size === size
      );
      if (exists) {
        return {
          items: state.items.map((i) =>
            i.product.id === product.id && i.size === size
              ? { ...i, quantity: i.quantity + quantity }
              : i
          ),
        };
      }
      return { items: [...state.items, { id: Date.now(), product, quantity, size }] };
    }

    case 'REMOVE_ITEM':
      return { items: state.items.filter((i) => i.id !== action.payload) };

    case 'UPDATE_QUANTITY':
      return {
        items: state.items.map((i) =>
          i.id === action.payload.id ? { ...i, quantity: action.payload.quantity } : i
        ),
      };

    case 'UPDATE_SIZE': {
      const { id, size } = action.payload;
      const source = state.items.find((i) => i.id === id);
      if (!source) return state;

      const duplicate = state.items.find(
        (i) => i.id !== id && i.product.id === source.product.id && i.size === size
      );
      if (duplicate) {
        return {
          items: state.items
            .filter((i) => i.id !== id)
            .map((i) => i.id === duplicate.id
              ? { ...i, quantity: i.quantity + source.quantity }
              : i),
        };
      }

      return {
        items: state.items.map((i) => i.id === id ? { ...i, size } : i),
      };
    }

    case 'CLEAR_CART':
      return initialState;

    default:
      return state;
  }
}

function loadFromStorage() {
  try {
    const data = localStorage.getItem('a1looks_cart');
    return data ? JSON.parse(data) : initialState;
  } catch {
    return initialState;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, loadFromStorage);

  useEffect(() => {
    localStorage.setItem('a1looks_cart', JSON.stringify(state));
  }, [state]);

  const cartCount = state.items.reduce((sum, i) => sum + i.quantity, 0);

  const subtotal = state.items.reduce(
    (sum, i) => sum + parseFloat(i.product.effective_price || i.product.price) * i.quantity,
    0
  );

  const shipping = subtotal >= 999 ? 0 : subtotal > 0 ? 99 : 0;

  // Tracks add-to-cart behaviour for on-device recommendations before dispatching.
  const addToCart = useCallback((product, quantity = 1, size = '') => {
    dispatch({ type: 'ADD_ITEM', payload: { product, quantity, size } });
    const categorySlug = product.category_slug || product.category?.slug;
    if (categorySlug) trackEvent(categorySlug, 'cart_add');
  }, [dispatch]);

  return (
    <CartContext.Provider
      value={{ items: state.items, cartCount, subtotal, shipping, total: subtotal + shipping, dispatch, addToCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
